import { useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/siteData';

function formatReadCount(count: number): string {
  if (count >= 10000) {
    return `${(count / 10000).toFixed(1)}万`;
  }
  return count.toString();
}

export default function CategoryFeatured() {
  const scrollContainersRef = useRef<Record<string, HTMLDivElement | null>>({});

  const setScrollRef = useCallback((slug: string) => (el: HTMLDivElement | null) => {
    scrollContainersRef.current[slug] = el;
  }, []);

  const scroll = (slug: string, direction: 'left' | 'right') => {
    const el = scrollContainersRef.current[slug];
    if (el) {
      el.scrollBy({
        left: direction === 'left' ? -320 : 320,
        behavior: 'smooth',
      });
    }
  };

  const getCategoryArticles = (categorySlug: string) => {
    return articles.filter((a) => a.category === categorySlug).slice(0, 6);
  };

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="px-4 md:px-6 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100 border border-accent-200/70 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>
            <span className="text-xs font-medium text-accent-700">分类精选</span>
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            按分类浏览爆料
          </h2>
          <p className="mt-2 text-sm text-foreground-500">
            四大板块全覆盖，总有一款瓜适合你
          </p>
        </div>

        <div className="flex flex-col gap-12">
          {categories.map((category) => {
            const catArticles = getCategoryArticles(category.slug);
            return (
              <div key={category.slug}>
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                      <i className={`${category.icon} text-primary-600 text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-bold text-foreground-900">
                        {category.name}
                      </h3>
                      <p className="text-xs text-foreground-500">{category.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => scroll(category.slug, 'left')}
                      className="w-8 h-8 flex items-center justify-center rounded-full border border-background-300 hover:bg-background-50 transition-colors duration-200 cursor-pointer"
                      aria-label="向左滚动"
                    >
                      <i className="ri-arrow-left-s-line text-foreground-500 text-sm"></i>
                    </button>
                    <button
                      onClick={() => scroll(category.slug, 'right')}
                      className="w-8 h-8 flex items-center justify-center rounded-full border border-background-300 hover:bg-background-50 transition-colors duration-200 cursor-pointer"
                      aria-label="向右滚动"
                    >
                      <i className="ri-arrow-right-s-line text-foreground-500 text-sm"></i>
                    </button>
                    <Link
                      to={`/category/${category.slug}`}
                      className="ml-2 whitespace-nowrap text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors duration-200"
                    >
                      查看全部
                      <i className="ri-arrow-right-line ml-0.5"></i>
                    </Link>
                  </div>
                </div>

                <div
                  ref={setScrollRef(category.slug)}
                  className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide"
                >
                  {catArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="flex-shrink-0 w-[240px] md:w-[260px] group cursor-pointer"
                    >
                      <div className="rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300">
                        <div className="aspect-[4/3] overflow-hidden">
                          <img
                            src={article.coverImage}
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-3">
                          <h4 className="text-xs md:text-sm font-bold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors duration-200">
                            {article.title}
                          </h4>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-foreground-400 flex items-center gap-1">
                              <i className="ri-eye-line"></i>
                              {formatReadCount(article.readCount)}
                            </span>
                            <span className="text-xs text-foreground-300">{article.publishDate}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}